// ---------------------------------------------------------------------------
// YouAreYou (uru) — AppLayout  (2026 rose refresh)
//
// The authenticated application shell.
// - Desktop: sticky top nav — uru logomark + links (coral active underline),
//   circular notification bell + profile button. Borderless, ambient shadow.
// - Mobile: top header + bottom tab bar (coral active).
//
// Visual language: screen-mockups/redesign/ (warm #faf9ff surface, monolithic
// white cards, coral #f08b8b actions, rose #974446 brand, ink #161b27 text).
//
// Realtime: NotificationProvider connects the WS, manages the inbox + toasts.
// ---------------------------------------------------------------------------

import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useMode } from "@/hooks/useMode";
import { useState, useRef, useEffect } from "react";
import BrandName from "@/components/ui/BrandName";
import BrandMark from "@/components/ui/BrandMark";
import { NotificationProvider } from "@/context/NotificationContext";
import { useNotifications } from "@/hooks/useNotifications";
import ToastContainer from "@/components/ui/Toast";
import { getUruMe } from "@/api/endpoints";
import { resolveAvatarUrl } from "@/types/api";
import { registerDeepLinkHandler } from "@/lib/deepLink";
import { initPushNotifications } from "@/lib/notifications";
import { isNative } from "@/lib/platform";

const CARD_SHADOW = "shadow-[0px_8px_32px_rgba(26,31,43,0.12)]";
const NAV_SHADOW = "shadow-[0px_4px_20px_rgba(26,31,43,0.04)]";

// ---------------------------------------------------------------------------
// Icon helper — renders Material Symbols Outlined
// ---------------------------------------------------------------------------

function Icon({
  name,
  filled = false,
  className = "",
}: {
  name: string;
  filled?: boolean;
  className?: string;
}) {
  return (
    <span
      className={`material-symbols-outlined select-none ${className}`}
      style={filled ? { fontVariationSettings: "'FILL' 1" } : undefined}
    >
      {name}
    </span>
  );
}

// ---------------------------------------------------------------------------
// uru logomark + wordmark
// ---------------------------------------------------------------------------

function Wordmark({ to }: { to: string }) {
  return (
    <NavLink to={to} className="flex items-center gap-2">
      <BrandMark className="h-8 w-8" />
      <span className="text-2xl font-extrabold lowercase tracking-tight text-[#161b27]">uru</span>
    </NavLink>
  );
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `border-b-2 pb-0.5 text-sm font-semibold transition-colors ${
    isActive
      ? "border-[#f08b8b] text-[#161b27]"
      : "border-transparent text-[#544242] hover:text-[#161b27]"
  }`;

// ---------------------------------------------------------------------------
// Desktop Top Navigation Bar
// ---------------------------------------------------------------------------

function DesktopNav() {
  const { user, logout } = useAuth();
  const { notifications, unreadCount, markRead } = useNotifications();
  const { mode, toggleMode } = useMode();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [uruDisplayName, setUruDisplayName] = useState<string>("");
  const [uruAvatarUrl, setUruAvatarUrl] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  // Fetch the KYC-sourced name and avatar from /api/uru/me
  useEffect(() => {
    getUruMe()
      .then((uruMe) => {
        const name = `${uruMe.user.first_name} ${uruMe.user.last_name}`.trim();
        if (name) setUruDisplayName(name);
        const url = resolveAvatarUrl(uruMe.user.avatar);
        if (url) setUruAvatarUrl(url);
      })
      .catch(() => {/* non-critical — fall back to auth user */});
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleNotifOpen() {
    setNotifOpen((prev) => !prev);
  }

  function handleNotifClick(notif: (typeof notifications)[0]) {
    void markRead(notif.id);
    setNotifOpen(false);

    // connection_id (e.g. connection_accepted) → open modal directly via route param
    const connId = notif.data?.connection_id;
    if (typeof connId === "number" || typeof connId === "string") {
      navigate(`/connections/${connId}`);
      return;
    }

    // connection_uuid (e.g. data_sharing_granted/requested/revoked) → pass UUID as
    // search param so ConnectionsPage can resolve it to a PK after loading connections.
    const connUuid = notif.data?.connection_uuid;
    if (typeof connUuid === "string") {
      navigate(`/connections?openUuid=${encodeURIComponent(connUuid)}`);
      return;
    }

    if (notif.action_url) {
      try {
        const url = new URL(notif.action_url);
        // Same-origin absolute URL — navigate within the SPA
        if (url.origin === window.location.origin) {
          navigate(url.pathname + url.search + url.hash);
        } else {
          window.open(notif.action_url, "_blank");
        }
      } catch {
        // Relative path (e.g. "/dashboard")
        navigate(notif.action_url);
      }
    }
  }

  const displayName =
    uruDisplayName ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.display_name ||
    "User";

  return (
    <header className={`sticky top-0 z-40 hidden items-center justify-between bg-white/85 px-8 py-3 backdrop-blur-md md:flex ${NAV_SHADOW}`}>
      {/* Left — Logo + nav links */}
      <div className="flex items-center gap-10">
        <Wordmark to={mode === "simple" ? "/quick-check" : "/dashboard"} />

        {mode === "advanced" && (
          <nav className="flex items-center gap-7">
            <NavLink to="/dashboard" className={navLinkClass}>Dashboard</NavLink>
            <NavLink to="/connections" className={navLinkClass}>Connections</NavLink>
            <NavLink to="/documents" className={navLinkClass}>DocsAreSigned</NavLink>
          </nav>
        )}
      </div>

      {/* Right — Notification bell + Profile */}
      <div className="flex items-center gap-3">
        {/* Notification bell — Advanced Mode only */}
        {mode === "advanced" && (
        <div className="relative" ref={notifRef}>
          <button
            onClick={handleNotifOpen}
            className="relative flex h-10 w-10 items-center justify-center rounded-full bg-[#faf9ff] text-[#544242] transition-colors hover:bg-[#f1f3ff]"
            aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
          >
            <Icon name="notifications" className="text-xl" />
            {unreadCount > 0 && (
              <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-[#974446] text-[9px] font-bold text-white">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <div className={`absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-2xl bg-white ${CARD_SHADOW}`}>
              <div className="border-b border-[#f1f3ff] px-4 py-3">
                <p className="text-sm font-bold text-[#161b27]">Notifications</p>
              </div>
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
                  <Icon name="notifications_none" className="text-3xl text-[#877271]" />
                  <p className="text-sm text-[#877271]">You&apos;re all caught up.</p>
                </div>
              ) : (
                <ul className="max-h-72 divide-y divide-[#f1f3ff] overflow-y-auto">
                  {notifications.map((notif) => (
                    <li
                      key={notif.id}
                      className={`flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-[#faf9ff] ${
                        notif.is_unread ? "bg-[#fef0f0]/50" : ""
                      }`}
                      onClick={() => handleNotifClick(notif)}
                    >
                      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#fef0f0]">
                        <Icon
                          name={
                            notif.kind === "alert"
                              ? "warning"
                              : notif.kind === "message"
                                ? "chat"
                                : "notifications"
                          }
                          filled
                          className="text-base text-[#974446]"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        {notif.title && (
                          <p className="truncate text-sm font-semibold text-[#161b27]">
                            {notif.title}
                          </p>
                        )}
                        <p className="line-clamp-2 text-sm text-[#544242]">
                          {notif.body}
                        </p>
                        <p className="mt-0.5 text-xs text-[#877271]">
                          {new Date(notif.created).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                      {notif.is_unread && (
                        <div className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#f08b8b]" />
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <div className="border-t border-[#f1f3ff] px-4 py-2">
                <NavLink
                  to="/connections"
                  onClick={() => setNotifOpen(false)}
                  className="text-xs font-bold text-[#974446] hover:text-[#792e30]"
                >
                  View all connections →
                </NavLink>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Profile */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-[#faf9ff] text-[#544242] ring-1 ring-[#dac1c0]/60 transition-colors hover:bg-[#f1f3ff]"
            aria-expanded={menuOpen}
            aria-haspopup="true"
            aria-label="Profile menu"
          >
            {uruAvatarUrl ? (
              <img src={uruAvatarUrl} alt={displayName} className="h-full w-full object-cover" />
            ) : (
              <Icon name="account_circle" className="text-2xl" />
            )}
          </button>

          {menuOpen && (
            <div className={`absolute right-0 top-full z-50 mt-2 w-52 overflow-hidden rounded-2xl bg-white ${CARD_SHADOW}`}>
              <div className="border-b border-[#f1f3ff] px-4 py-3">
                <NavLink
                  to="/settings"
                  onClick={() => setMenuOpen(false)}
                  className="block text-sm font-semibold text-[#161b27] transition-colors hover:text-[#974446]"
                >
                  {displayName}
                </NavLink>
                <p className="text-xs text-[#877271]">{user?.phone_number}</p>
              </div>
              <NavLink
                to="/settings"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#544242] hover:bg-[#faf9ff] hover:text-[#161b27]"
              >
                <Icon name="verified_user" className="text-lg" />
                Identity &amp; Settings
              </NavLink>
              <button
                onClick={() => {
                  toggleMode();
                  setMenuOpen(false);
                  navigate(mode === "simple" ? "/dashboard" : "/quick-check", { replace: true });
                }}
                className="flex w-full items-center gap-2 border-t border-[#f1f3ff] px-4 py-2.5 text-sm text-[#544242] hover:bg-[#faf9ff]"
              >
                <Icon
                  name={mode === "advanced" ? "toggle_on" : "toggle_off"}
                  filled={mode === "advanced"}
                  className={`text-lg ${mode === "advanced" ? "text-[#f08b8b]" : "text-[#877271]"}`}
                />
                {mode === "simple" ? "Switch to Advanced Mode" : "Switch to Simple Mode"}
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  logout();
                }}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-[#ba1a1a] hover:bg-[#faf9ff]"
              >
                <Icon name="logout" className="text-lg" />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Mobile Bottom Tab Bar
// ---------------------------------------------------------------------------

interface TabItem {
  label: string;
  icon: string;
  to: string;
  badge?: boolean;
}

const BASE_TABS: TabItem[] = [
  { label: "Contacts", icon: "contacts", to: "/connections" },
  { label: "Create Link", icon: "add_link", to: "/dashboard" },
  { label: "Notifications", icon: "notifications", to: "/requests", badge: false },
  { label: "Settings", icon: "settings", to: "/settings" },
];

function SimpleMobileCopyBar() {
  const [globalLink, setGlobalLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    getUruMe()
      .then((me) => { if (me.global_link) setGlobalLink(me.global_link); })
      .catch(() => {/* non-critical */});
  }, []);

  async function handleCopy() {
    if (!globalLink) return;
    try {
      await navigator.clipboard.writeText(globalLink);
    } catch {
      const el = document.createElement("textarea");
      el.value = globalLink;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className={`fixed inset-x-0 bottom-0 z-40 px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3 md:hidden ${
      isNative ? "bg-white/80 backdrop-blur-xl" : "bg-white " + NAV_SHADOW
    }`}>
      <button
        onClick={() => void handleCopy()}
        disabled={!globalLink}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#f08b8b] py-3.5 text-sm font-bold text-white shadow-sm transition-colors active:bg-[#d97a7c] disabled:opacity-40"
      >
        <Icon
          name={copied ? "check" : "content_copy"}
          filled={copied}
          className="text-lg"
        />
        {copied ? "Copied!" : "Copy Link"}
      </button>
    </div>
  );
}

function MobileTabBar() {
  const location = useLocation();
  const { unreadCount } = useNotifications();
  const { mode } = useMode();

  if (mode === "simple") return <SimpleMobileCopyBar />;

  const TABS = BASE_TABS.map((t) =>
    t.to === "/requests" ? { ...t, badge: unreadCount > 0 } : t,
  );

  return (
    <nav className={`fixed inset-x-0 bottom-0 z-40 pb-[env(safe-area-inset-bottom)] md:hidden ${
      isNative ? "bg-white/80 backdrop-blur-xl" : "bg-white " + NAV_SHADOW
    }`}>
      <div className="flex items-center justify-around">
        {TABS.map((tab) => {
          const isActive =
            location.pathname === tab.to ||
            (tab.to !== "/" && location.pathname.startsWith(tab.to));

          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={`relative flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors ${
                isActive
                  ? "text-[#f08b8b]"
                  : "text-[#877271] hover:text-[#544242]"
              }`}
              aria-current={isActive ? "page" : undefined}
            >
              <span className="relative">
                <Icon name={tab.icon} filled={isActive} className="text-2xl" />
                {tab.badge && (
                  <span className="absolute -right-1 -top-0.5 h-2 w-2 rounded-full bg-[#974446]" />
                )}
              </span>
              <span>{tab.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Mobile Top Header
// ---------------------------------------------------------------------------

const FAQ_ITEMS = [
  {
    q: "Will the person I'm asking actually do this?",
    a: "Most people do — especially when they understand it's mutual. You can let them know you're verified too, and that it takes about 3 minutes on their phone.",
  },
  {
    q: "What does the other person have to do?",
    a: "They click your Trust Link, verify their phone number, then complete a quick ID check: a photo of their government ID and a short selfie video. It usually takes 2–3 minutes.",
  },
  {
    q: "Can they see my documents?",
    a: "No. Neither party ever sees the other's documents. YouAreYou confirms real identities without exposing them.",
  },
  {
    q: "Is my data safe?",
    a: "Identity verification is handled by MojoVerify, an accredited identity verification provider. Your documents are processed securely and never stored on YouAreYou's servers.",
  },
  {
    q: "What's a Trust Link?",
    a: "It's a short link you share with someone before you meet. When they click it and complete ID verification, you both get confirmation of each other's real identity.",
  },
  {
    q: "What's the difference between a direct link and a global link?",
    a: "A direct link is sent to one specific person (by phone or email) and can only be used once. A global link can be shared with anyone and used multiple times — good for QR codes or listing profiles.",
  },
];

function HelpModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-[#faf9ff]"
      role="dialog"
      aria-modal="true"
      aria-label="Help & FAQ"
    >
      <div className="flex items-center justify-between border-b border-[#e9edfe] bg-white px-4 py-3">
        <h2 className="text-base font-bold text-[#161b27]">Help &amp; FAQ</h2>
        <button
          onClick={onClose}
          className="rounded-full p-1.5 text-[#544242] hover:bg-[#f1f3ff]"
          aria-label="Close help"
        >
          <Icon name="close" className="text-xl" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <ul className="space-y-3">
          {FAQ_ITEMS.map((item) => (
            <li key={item.q} className={`rounded-xl bg-white p-4 ${NAV_SHADOW}`}>
              <p className="text-sm font-semibold text-[#161b27]">{item.q}</p>
              <p className="mt-1.5 text-sm text-[#544242]">{item.a}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function MobileHeader() {
  const [showHelp, setShowHelp] = useState(false);
  const { mode } = useMode();

  // On iOS the persistent web-style header is hidden — native apps use per-page
  // titles. Safe area inset is handled by padding on the main content area instead.
  if (isNative) return null;

  return (
    <>
      <header
        className={`flex items-center justify-between bg-white/85 px-4 pb-3 backdrop-blur-md md:hidden ${NAV_SHADOW}`}
        style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top))' }}
      >
        <NavLink to={mode === "simple" ? "/quick-check" : "/dashboard"} className="flex items-center gap-2">
          <BrandMark className="h-7 w-7" />
          <span className="text-lg font-extrabold lowercase tracking-tight text-[#161b27]">uru</span>
        </NavLink>
        <button
          className="rounded-full p-1.5 text-[#544242] hover:bg-[#f1f3ff]"
          aria-label="Help"
          onClick={() => setShowHelp(true)}
        >
          <Icon name="help_outline" className="text-xl" />
        </button>
      </header>
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// AppLayoutInner — consumes NotificationContext (must be inside the provider)
// ---------------------------------------------------------------------------

function AppLayoutInner() {
  const { status } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useNotifications();

  // Register the Universal Link (deep link) handler once on mount.
  // On web, registerDeepLinkHandler is a no-op.
  useEffect(() => {
    registerDeepLinkHandler(navigate);
  }, [navigate]);

  // Register for push notifications once the user is authenticated.
  // On web, initPushNotifications is a no-op.
  useEffect(() => {
    if (!isNative || status !== "authenticated") return;
    void initPushNotifications(
      // Foreground notification → show as in-app toast
      (notification) => {
        addToast({
          message: [notification.title, notification.body].filter(Boolean).join(': '),
          kind: 'info',
          actionUrl: '/requests',
        });
      },
      navigate,
    );
  }, [status, navigate, addToast]);

  return (
    <div className="flex min-h-dvh flex-col bg-[#faf9ff]">
      {/* Desktop: top nav */}
      <DesktopNav />

      {/* Mobile: simple top header */}
      <MobileHeader />

      {/* Main content area */}
      {/* On iOS: header is hidden so we pad the top by the status bar height instead */}
      <main className={`flex-1 pb-20 md:pb-0 ${isNative ? "pt-[env(safe-area-inset-top)]" : ""}`}>
        <Outlet />
      </main>

      {/* Mobile: bottom tab bar */}
      <MobileTabBar />

      {/* Ephemeral toast notifications */}
      <ToastContainer />

      {/* Footer — desktop only */}
      <footer className={`hidden items-center justify-between bg-white px-8 py-4 text-xs text-[#877271] md:flex ${NAV_SHADOW}`}>
        <div className="flex items-center gap-2">
          <div className="flex h-5 w-5 items-center justify-center rounded bg-[#f1f3ff]">
            <Icon name="shield" className="text-xs text-[#877271]" />
          </div>
          <span>Powered by MojoVerify</span>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/privacy" className="transition-colors hover:text-[#544242]">
            Privacy Policy
          </Link>
          <Link to="/terms" className="transition-colors hover:text-[#544242]">
            Terms of Service
          </Link>
          <Link to="/support" className="transition-colors hover:text-[#544242]">
            Support
          </Link>
        </div>
        <span>&copy; {new Date().getFullYear()} <BrandName showBadge={false} />. All rights reserved.</span>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AppLayout — wraps inner layout with NotificationProvider
// ---------------------------------------------------------------------------

export default function AppLayout() {
  return (
    <NotificationProvider>
      <AppLayoutInner />
    </NotificationProvider>
  );
}
